import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Lock, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import SenArtisteLogo from "@/components/SenArtisteLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error

  useEffect(() => {
    if (!token) {
      setStatus("error");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        toast.success("Mot de passe modifié avec succès !");
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        if (data.detail?.includes("expiré")) {
          toast.error("Le lien a expiré. Demandez un nouveau lien.");
        } else if (data.detail?.includes("invalide")) {
          toast.error("Lien invalide ou déjà utilisé");
        } else {
          toast.error(data.detail || "Une erreur est survenue");
        }
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 hero-gradient">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(/images/concert-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Back Button */}
      <Link
        to="/login"
        className="fixed top-4 left-4 z-50 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <Card className="glass-card">
          <CardHeader className="text-center space-y-2">
            <Link to="/" className="flex items-center justify-center gap-3 mb-4">
              <SenArtisteLogo className="w-16 h-16" />
              <span className="font-syne font-bold text-2xl">Sen Artiste</span>
            </Link>
            <CardTitle className="font-syne text-2xl">
              {status === "success" ? "Mot de passe modifié" : "Nouveau mot de passe"}
            </CardTitle>
            <CardDescription>
              {status === "success" 
                ? "Vous pouvez maintenant vous connecter"
                : status === "error"
                ? "Le lien est invalide ou a expiré"
                : "Choisissez un nouveau mot de passe sécurisé"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {status === "success" && (
              <div className="text-center py-6" data-testid="reset-success">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">C'est fait !</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  Votre mot de passe a été modifié avec succès.
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Redirection vers la connexion dans 3 secondes...
                </p>
                <Link to="/login">
                  <Button className="w-full btn-primary">
                    Se connecter maintenant
                  </Button>
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-6" data-testid="reset-error">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Lien invalide</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  Ce lien de réinitialisation est invalide ou a déjà été utilisé.
                </p>
                <Link to="/forgot-password">
                  <Button className="w-full btn-primary">
                    Demander un nouveau lien
                  </Button>
                </Link>
              </div>
            )}

            {status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification...
                    </>
                  ) : (
                    "Modifier le mot de passe"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
